import React, { useState, useRef, useEffect } from "react";
import Editor from "@monaco-editor/react";
import {
    Box,
    Paper,
    Grid,
    Tabs,
    Tab,
    IconButton,
    Button,
    List,
    ListItemButton,
    ListItemText,
    Divider,
    Typography,
    TextField,
    Menu,
    MenuItem,
    useTheme,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";

// Path to uploaded design screenshot (local path from your session)
const designScreenshot = "/mnt/data/938218ce-646b-4ea0-8953-339a4299bd9e.png";

// Minimal language -> Judge0 language_id map (expand as needed)
const LANGUAGE_MAP = {
    C: 50, // C (example id) - change if you self-host Judge0 with different ids
    "C++": 54,
    JAVA: 62,
    Python: 71,
    JavaScript: 63,
};

const DEFAULT_FILE = {
    C: { name: "main.c", ext: ".c", monaco: "c" },
    "C++": { name: "main.cpp", ext: ".cpp", monaco: "cpp" },
    JAVA: { name: "Main.java", ext: ".java", monaco: "java" },
    Python: { name: "main.py", ext: ".py", monaco: "python" },
    JavaScript: { name: "main.js", ext: ".js", monaco: "javascript" },
};

export default function MonacoCodingLab() {
    const theme = useTheme();
    const [selectedLang, setSelectedLang] = useState("C");
    const [files, setFiles] = useState([{ name: "Main.c", language: "c", value: '#include <stdio.h>\nint main() {\n  printf("Hello, World!\");\n  return 0;\n}' }]);
    const [activeFile, setActiveFile] = useState(0);
    const [output, setOutput] = useState("Output will appear here...");
    const [stdin, setStdin] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const monacoRef = useRef(null);

    useEffect(() => {
        // adjust initial layout or fetch problem data here
    }, []);

    function handleEditorMount(editor, monaco) {
        monacoRef.current = editor;
    }

    function updateFileValue(value) {
        setFiles((prev) => {
            const next = [...prev];
            next[activeFile] = { ...next[activeFile], value };
            return next;
        });
    }

    function addNewFile() {
        setFiles((prev) => [...prev, { name: `file${prev.length + 1}.c`, language: "c", value: "" }]);
        setActiveFile(files.length);
    }

    function getCommentPrefix(lang) {
        if (lang === "Python") return "#";
        return "//";
    }


    function handleChangeLanguage(lang) {
        setSelectedLang(lang);

        const fileInfo = DEFAULT_FILE[lang];

        setFiles([
            {
                name: fileInfo.name,
                language: fileInfo.monaco,
                value: "// Write your code here...python"
            }
        ]);

        setActiveFile(0);
    }

    async function runCode() {
        setOutput("Running...");

        // For quick demo use Judge0 public CE endpoint
        const apiUrl = "https://ce.judge0.com/submissions?base64_encoded=false&wait=true";

        // Pick language id from map, fallback to C
        const langId = LANGUAGE_MAP[selectedLang] || 50;

        // For multi-file projects you may need to join files or create a single entry
        const commentPrefix = getCommentPrefix(selectedLang);

        const source_code = files
            .map((f) => `${commentPrefix} File: ${f.name}\n${f.value}\n`)
            .join("\n");


        try {
            const res = await fetch(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ language_id: langId, source_code, stdin }),
            });

            if (!res.ok) {
                const text = await res.text();
                throw new Error(`Judge0 error: ${text}`);
            }

            const data = await res.json();
            // Judge0 returns fields: stdout, stderr, compile_output
            const out = data.stdout || data.stderr || data.compile_output || "(no output)";
            setOutput(out);
        } catch (err) {
            setOutput(String(err));
        }
    }

    return (
        <Box sx={{ p: 2, backgroundColor: theme.palette.background.default }}>
            <Paper elevation={3} sx={{ borderRadius: 2, p: 1 }}>
                <Grid container>
                    {/* LEFT SIDEBAR - Languages */}
                    <Grid size={2} sx={{ borderRight: `1px solid ${theme.palette.divider}`, pr: 1 }}>
                        <List>
                            {Object.keys(LANGUAGE_MAP).map((lang) => (
                                <ListItemButton
                                    key={lang}
                                    selected={selectedLang === lang}
                                    onClick={() => handleChangeLanguage(lang)}

                                    sx={{ borderRadius: 1, mb: 1, color: theme.palette.text.secondary }}
                                >
                                    <ListItemText primary={lang} />
                                </ListItemButton>
                            ))}
                        </List>
                        <Divider />
                        {/* <Box sx={{ mt: 1, px: 1 }}>
              <img src={designScreenshot} alt="design" style={{ width: "100%", borderRadius: 6 }} />
            </Box> */}
                    </Grid>

                    {/* MAIN EDITOR + OUTPUT */}
                    <Grid size={10} sx={{ p: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Tabs value={activeFile} onChange={(e, v) => setActiveFile(v)}>
                                    {files.map((f, idx) => (
                                        <Tab key={f.name} label={f.name} />
                                    ))}
                                    <Tab icon={<AddIcon />} onClick={addNewFile} />
                                </Tabs>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                {/* <TextField size="small" placeholder="stdin (optional)" value={stdin} onChange={(e) => setStdin(e.target.value)} /> */}

                                <Button variant="contained" sx={{
                                    borderRadius: "50%",
                                    minWidth: "32px",
                                    width: "32px",
                                    height: "32px",
                                    p: 0,
                                }} onClick={runCode}>
                                    <PlayArrowIcon />
                                </Button>

                                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                                    <MoreVertIcon />
                                </IconButton>

                                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                                    <MenuItem onClick={() => { navigator.clipboard.writeText(files[activeFile].value); setAnchorEl(null); }}>Copy code</MenuItem>
                                    <MenuItem onClick={() => { setFiles([{ name: "Main.c", language: "c", value: "" }]); setActiveFile(0); setAnchorEl(null); }}>Reset</MenuItem>
                                </Menu>
                            </Box>
                        </Box>

                        <Paper sx={{ borderRadius: 2, height: 520, overflow: "hidden", display: "flex", backgroundColor: theme.palette.background.default }}>
                            <Box sx={{ flex: 1, }}>
                                <Editor
                                    height="100%"
                                    defaultLanguage={selectedLang.toLowerCase()}
                                    value={files[activeFile]?.value}
                                    onMount={handleEditorMount}
                                    onChange={updateFileValue}
                                    options={{ minimap: { enabled: false }, fontSize: 13 }}
                                />
                            </Box>

                            <Divider orientation="vertical" flexItem />

                            <Box sx={{ width: 360, p: 2 }}>
                                <Typography variant="subtitle2">Output:</Typography>
                                <Paper variant="outlined" sx={{ mt: 1, p: 1, height: 420, overflow: "auto", backgroundColor: theme.palette.action.selected }}>
                                    <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{output}</pre>
                                </Paper>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
}
